/**
 * Shell DESKTOP — la app en el PC, para analizar.
 *
 * No es la vista mobile estirada. Diferencias que importan:
 * - Barra lateral con LOS NUEVE destinos visibles a la vez. En el celular hay cinco tabs porque
 *   no cabe más; acá esconder mitad de la app detrás de un menú sería desperdiciar el ancho.
 * - Pero nueve destinos en una lista plana no son una navegación, son un inventario: hay que
 *   leerlos todos para encontrar uno. Van en dos grupos con rótulo, Registrar y Mirar, que es la
 *   misma división que hace la app (una pantalla escribe datos o los consulta, §4 del flujo).
 * - Atajos de teclado de una tecla. Quien analiza su progreso en el PC pasa de pantalla en
 *   pantalla muchas veces, y alcanzar el mouse cada vez es fricción pura.
 * - El contenido se despliega en rejilla: dos, tres o cuatro columnas según la pantalla, con
 *   tablas y gráficos grandes en vez de tarjetas apiladas.
 */

import { useEffect, type ReactNode } from 'react';

import { PANTALLAS, useRuta, type Pantalla } from './router.tsx';
import { Icono, type NombreIcono } from './componentes.tsx';
import { IndicadorSync } from './IndicadorSync.tsx';
import { useVariante } from './layout-variant.tsx';
import { BotonAyuda } from './Ayuda.tsx';

interface Destino {
  pantalla: Pantalla;
  icono: NombreIcono;
  tecla: string;
}

/** Inicio va suelto arriba: no es un destino de análisis más, es la puerta que dice qué toca hoy. */
const INICIO: Destino = { pantalla: 'inicio', icono: 'casa', tecla: 'i' };

const GRUPOS: { rotulo: string; destinos: Destino[] }[] = [
  {
    rotulo: 'Registrar',
    destinos: [
      { pantalla: 'registro', icono: 'mancuerna', tecla: 'r' },
      { pantalla: 'nutricion', icono: 'comida', tecla: 'n' },
      { pantalla: 'cuerpo', icono: 'balanza', tecla: 'c' },
      { pantalla: 'ingesta', icono: 'camara', tecla: 'f' },
    ],
  },
  {
    rotulo: 'Mirar',
    destinos: [
      { pantalla: 'calendario', icono: 'calendario', tecla: 'p' },
      { pantalla: 'progreso', icono: 'progreso', tecla: 'g' },
      { pantalla: 'historial', icono: 'historial', tecla: 'h' },
    ],
  },
];

/** Ni registra ni se mira: configura. Va al fondo, donde no compite con lo de todos los días. */
const AJUSTES: Destino = { pantalla: 'ajustes', icono: 'ajustes', tecla: 'a' };

/**
 * Los atajos salen de la misma lista que los botones, así que agrupar no puede cambiarlos: la
 * tecla de cada pantalla es la que ya estaba en los dedos de quien la usa.
 */
const NAV: Destino[] = [INICIO, ...GRUPOS.flatMap((g) => g.destinos), AJUSTES];

export function ShellDesktop({ children }: { children: ReactNode }): ReactNode {
  const { pantalla, ir } = useRuta();
  const { fijar } = useVariante();

  useEffect(() => {
    const alTeclear = (e: KeyboardEvent) => {
      // Nunca robar teclas mientras alguien escribe: un "r" dentro de un campo de peso es un "r".
      const objetivo = e.target as HTMLElement | null;
      if (objetivo && /^(INPUT|TEXTAREA|SELECT)$/.test(objetivo.tagName)) return;
      if (objetivo?.isContentEditable) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      // Ni mientras hay un diálogo abierto: leyendo la ayuda, la "h" de "hombro" navegaba a
      // Historial por detrás del panel y al cerrarlo aparecía otra pantalla.
      if (document.querySelector('[role="dialog"]')) return;

      const encontrada = NAV.find((n) => n.tecla === e.key.toLowerCase());
      if (encontrada) {
        e.preventDefault();
        ir(encontrada.pantalla);
      }
    };

    window.addEventListener('keydown', alTeclear);
    return () => window.removeEventListener('keydown', alTeclear);
  }, [ir]);

  return (
    <div className="d-app">
      <aside className="d-lateral">
        <div className="d-marca">
          <span className="titulo" style={{ letterSpacing: '-0.04em' }}>
            mg
          </span>
          <span className="nota--fina">entrenamiento y composición</span>
        </div>

        <nav className="d-nav" aria-label="Secciones">
          <ItemNav destino={INICIO} actual={pantalla} ir={ir} />

          {GRUPOS.map((g) => (
            <div key={g.rotulo} className="d-nav-grupo">
              <span className="eyebrow d-nav-rotulo">{g.rotulo}</span>
              {g.destinos.map((d) => (
                <ItemNav key={d.pantalla} destino={d} actual={pantalla} ir={ir} />
              ))}
            </div>
          ))}

          <ItemNav destino={AJUSTES} actual={pantalla} ir={ir} />
        </nav>

        <div className="crece" />

        <button
          type="button"
          className="d-nav-item"
          onClick={() => fijar('mobile')}
          title="Ver la interfaz de gimnasio desde el PC, sin sacar el teléfono"
        >
          <Icono nombre="mobile" />
          Ver como celular
        </button>
      </aside>

      <div className="d-principal">
        <header className="d-cabecera">
          <h1 className="titulo">{PANTALLAS[pantalla].titulo}</h1>
          <div className="fila" style={{ gap: 'var(--e3)' }}>
            <IndicadorSync />
            <BotonAyuda variante="desktop" />
          </div>
        </header>
        <main className="d-cuerpo">{children}</main>
      </div>
    </div>
  );
}

function ItemNav({
  destino,
  actual,
  ir,
}: {
  destino: Destino;
  actual: Pantalla;
  ir: (pantalla: Pantalla) => void;
}): ReactNode {
  return (
    <button
      type="button"
      className="d-nav-item"
      onClick={() => ir(destino.pantalla)}
      {...(actual === destino.pantalla ? { 'aria-current': 'page' as const } : {})}
    >
      <Icono nombre={destino.icono} />
      {PANTALLAS[destino.pantalla].titulo}
      <kbd className="d-nav-tecla">{destino.tecla.toUpperCase()}</kbd>
    </button>
  );
}
